import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { AppointmentBookingForm as AppointmentBookingFormSchema } from '@amplience/content-types/typings/c-appointment-booking-form';
import { getStoreLocatorBaseUrl } from '@storefront/lib-global';
import { DatepickerVariant } from '@storefront/lib-global/baseComponents/datepicker/DatepickerVariant';
import { Loader } from '@storefront/lib-global/globalComponents/Loader';
import { LocationComponent, LocationErrorReason } from '@storefront/lib-global/globalComponents/locationComponent';
import type {
  LocationComponentHandle,
  LocationDataType,
  LocationErrorType,
} from '@storefront/lib-global/globalComponents/locationComponent';
import { setAppointmentBookingPagePath } from '@storefront/lib-global/utils/appointmentUtils';
import { formatISODateRepresentationDate } from '@storefront/lib-global/utils/dateUtils/formatISODateRepresentationDate';
import { sendBookAFittingEvent } from '@storefront/lib-global/utils/gtm/events/book_a_fitting/sendBookAFittingEvent';
import { useNumberOfChildrenInput } from '../../hooks';
import type { NumberOfChildrenLabelType } from '../inputs';
import { MAX_NUMBER_OF_CHILDREN, NumberOfChildren, NumberOfChildrenLabelVariant } from '../inputs';
import { Button, Container, Fields, InputContainer, Message } from './AppointmentBookingForm.styles';

const DatepickerComponent = dynamic(
  () =>
    // eslint-disable-next-line promise/prefer-await-to-then
    import('@storefront/lib-global/baseComponents/datepicker/Datepicker').then((mod) => mod.DatepickerComponent),
  { ssr: false },
);

export type AppointmentBookingFormProps = AppointmentBookingFormSchema;

export type InitialAppointmentBookingFormValues = {
  lat: number | null;
  lng: number | null;
  childrenCount: number;
  locationQuery: string;
  date: Date | null;
};

export type AppointmentBookingFormData = {
  lat: number;
  lng: number;
  childrenCount: number;
  locationQuery?: string;
  selectedDate: Date | null;
};

export type AppointmentBookingFormSubmitParams = {
  lat: string;
  lng: string;
  childrenCount: string;
  locationQuery?: string;
  date?: string;
};

export type AppointmentBookingFormComponentProps = {
  isFullWidth?: boolean;
  isLeftAligned?: boolean;
  isAmplience?: boolean;
  storeLocatorLink?: string;
  numberOfChildrenLabel?: NumberOfChildrenLabelType;
  datepickerVariant?: DatepickerVariant;
  buttonLabelKey?: string;
  initialValues?: InitialAppointmentBookingFormValues;
  onSubmit: (params: AppointmentBookingFormData) => void;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  isLoading?: boolean;
};

export type AppointmentBookingFormErrors = {
  location?: LocationErrorType;
  missingDate: boolean;
  maxChildren: boolean;
};

export function AppointmentBookingFormComponent({
  isFullWidth = false,
  isLeftAligned = false,
  isAmplience = false,
  storeLocatorLink,
  numberOfChildrenLabel = NumberOfChildrenLabelVariant.DEFAULT,
  datepickerVariant = DatepickerVariant.DEFAULT,
  buttonLabelKey = 'appointment.book.a.fitting',
  initialValues = {
    lat: null,
    lng: null,
    locationQuery: '',
    childrenCount: 1,
    date: null,
  },
  onSubmit,
  isPDP = false,
  isLoading,
  setIsLoading,
}: AppointmentBookingFormComponentProps & { isPDP?: boolean }) {
  const { t } = useTranslation(['lib-appointments-common', 'app-orders-common']);
  const { EMPTY_LOCATION, INVALID_LOCATION } = LocationErrorReason;
  const { childrenCount, handleIncreaseChildrenCount, handleDecreaseChildrenCount } = useNumberOfChildrenInput({
    initialValue: initialValues.childrenCount,
  });
  const { locale } = useRouter();
  const storeLocatorUrl = storeLocatorLink || `/${getStoreLocatorBaseUrl(locale)}`;

  const [selectedDate, setSelectedDate] = useState<Date | null>(initialValues.date);
  const [buttonClicked, setButtonClicked] = useState(false);
  const locationRef = useRef<LocationComponentHandle>(null);
  const initialLocation =
    initialValues.lat && initialValues.lng
      ? {
          lat: initialValues.lat,
          lng: initialValues.lng,
          locationQuery: initialValues.locationQuery,
        }
      : undefined;

  const [errors, setErrors] = useState<Partial<AppointmentBookingFormErrors>>({
    location: { error: false },
    missingDate: false,
    maxChildren: false,
  });

  useEffect(() => {
    setErrors((prevState) => ({
      ...prevState,
      maxChildren: childrenCount > MAX_NUMBER_OF_CHILDREN,
    }));
  }, [childrenCount]);

  const validateDate = (date: Date | null) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const dateError = !date || date < tomorrow;
    setErrors((prevState) => ({
      ...prevState,
      missingDate: dateError,
    }));
    return !dateError;
  };
  const handleChangeDate = (date: Date) => {
    validateDate(date);
    setSelectedDate(date);
  };

  const getErrorMessage = () => {
    if (buttonClicked) {
      if (errors?.location?.error && EMPTY_LOCATION === errors?.location?.reason) {
        return t(`appointment.missing.${errors.missingDate ? 'fields' : 'location'}.message`);
      }

      if (errors?.location?.error && INVALID_LOCATION === errors?.location?.reason) {
        return t('appointment.location.not.found.message');
      }

      if (errors.missingDate) {
        return t('appointment.missing.date.message');
      }
    }
    if (errors.maxChildren) {
      return (
        <>
          {t('appointment.number.of.children.overbooking.message')} -{' '}
          <Link href={`${storeLocatorUrl}`}>{t('appointment.store.locator')}</Link>
        </>
      );
    }

    return null;
  };

  const proceedToSubmit = ({ lat, lng, locationQuery }: LocationDataType) => {
    sendBookAFittingEvent({
      number_of_children: childrenCount,
      date: selectedDate ? formatISODateRepresentationDate(selectedDate, locale, '') : '',
      fitting_location: 'in store fitting',
      postcode: locationQuery || 'Current Location',
    });
    onSubmit({
      lat,
      lng,
      locationQuery,
      childrenCount,
      selectedDate,
    });
  };

  const hasErrors = errors?.location?.error || errors.missingDate || errors.maxChildren;
  const forceValidation = () => {
    if (!buttonClicked) {
      const isValidDate = validateDate(selectedDate);
      const isValidLocation = locationRef.current?.forceValidate();
      return isValidDate && isValidLocation;
    }
    return !hasErrors;
  };

  const handleProceed = async () => {
    setButtonClicked(true);

    if (!isLoading && forceValidation()) {
      if (setIsLoading) {
        setIsLoading(true);
      }

      const locationData = await locationRef?.current?.getLocation();
      if (locationData) {
        proceedToSubmit(locationData);
      }

      if (setIsLoading) {
        setIsLoading(false);
      }
    }
  };

  const message = getErrorMessage();
  const buttonDisabled = (buttonClicked && hasErrors) || errors.maxChildren;

  return (
    <Container $widthLimit={!isLeftAligned} $isAmplience={isAmplience}>
      <Fields>
        <InputContainer $fullWidth>
          <LocationComponent
            ref={locationRef}
            initialLocation={initialLocation}
            errors={errors}
            setErrors={setErrors}
            showError={false}
            isRequired
          />
        </InputContainer>
        <InputContainer $fullWidth={!!isPDP} $halfWidth={isLeftAligned}>
          <DatepickerComponent
            variant={datepickerVariant}
            isFullWidth
            selected={selectedDate}
            onChange={handleChangeDate}
            isInputError={errors.missingDate}
            isRequired
            data-testid="bookingDatepicker"
          />
        </InputContainer>
        <InputContainer $fullWidth={!!isPDP} $halfWidth={isLeftAligned}>
          <NumberOfChildren
            labelType={numberOfChildrenLabel}
            value={childrenCount}
            onDecrease={handleDecreaseChildrenCount}
            onIncrease={handleIncreaseChildrenCount}
          />
        </InputContainer>
      </Fields>
      {message && <Message>{message}</Message>}
      <Button
        $fullWidth={isFullWidth || !!isPDP}
        $alignLeft={isLeftAligned}
        onClick={handleProceed}
        disabled={buttonDisabled}
        className="book-a-fitting-button"
        data-testid="bookAFittingButton"
      >
        {isLoading ? <Loader /> : t(buttonLabelKey)}
      </Button>
    </Container>
  );
}

export function AppointmentBookingForm(props: AppointmentBookingFormProps) {
  const router = useRouter();
  const redirectUrl = '/book-a-fitting';

  const { isPDP } = props;

  const isAmplience = !isPDP;

  const onSubmit = ({
    lat: latitude,
    lng: longitude,
    locationQuery: location,
    childrenCount: count,
    selectedDate,
  }: AppointmentBookingFormData) => {
    const params: AppointmentBookingFormSubmitParams = {
      lat: latitude.toString(),
      lng: longitude.toString(),
      childrenCount: count.toString(),
      ...(location && { location }),
      ...(selectedDate && { date: formatISODateRepresentationDate(selectedDate, router.locale) }),
    };

    setAppointmentBookingPagePath(router.asPath);
    const queryParams = new URLSearchParams(params);
    router.push(`${redirectUrl}?${queryParams}`);
  };

  return <AppointmentBookingFormComponent {...props} isAmplience={isAmplience} onSubmit={onSubmit} />;
}
